import jsPDF from 'jspdf';
import type { Receipt, Invoice, Payment, Patient } from './supabase';


export const generateReceiptPDF = (
  receipt: Receipt,
  template: 'standard' | 'detailed' | 'summary' = 'standard'
) => {
  const doc = new jsPDF();
  
  switch (template) {
    case 'standard':
      return generateStandardReceipt(doc, receipt);
    case 'detailed':
      return generateDetailedReceipt(doc, receipt);
    case 'summary':
      return generateSummaryReceipt(doc, receipt);
    default:
      return generateStandardReceipt(doc, receipt);
  }
};

const generateStandardReceipt = (doc: jsPDF, receipt: Receipt) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('PAYMENT RECEIPT', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 15;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('MedSystem Hospital Management', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 20;
  
  doc.setDrawColor(20, 184, 166); // Teal color
  doc.setLineWidth(0.5);
  doc.rect(15, yPos, pageWidth - 30, 25);
  
  yPos += 8;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`Receipt No: ${receipt.receipt_number}`, 20, yPos);
  
  yPos += 7;
  doc.setFont('helvetica', 'normal');
  doc.text(`Date: ${new Date(receipt.generated_at).toLocaleDateString()}`, 20, yPos);
  
  yPos += 7;
  doc.text(`Time: ${new Date(receipt.generated_at).toLocaleTimeString()}`, 20, yPos);
  
  yPos += 15;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Patient Information', 20, yPos);
  yPos += 7;
  
  doc.setFont('helvetica', 'normal');
  if (receipt.patient) {
    doc.text(`Name: ${receipt.patient.name}`, 20, yPos);
    yPos += 6;
    if (receipt.patient.unique_id) {
      doc.text(`Patient ID: ${receipt.patient.unique_id}`, 20, yPos);
      yPos += 6;
    }
    if (receipt.patient.phone) {
      doc.text(`Phone: ${receipt.patient.phone}`, 20, yPos);
      yPos += 6;
    }
  }
  
  yPos += 10;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Details', 20, yPos);
  yPos += 7;
  
  doc.setFont('helvetica', 'normal');
  if (receipt.invoice) {
    doc.text(`Invoice No: ${receipt.invoice.invoice_number}`, 20, yPos);
    yPos += 6;
  }
  
  if (receipt.payment_method) {
    doc.text(`Payment Method: ${receipt.payment_method.toUpperCase()}`, 20, yPos);
    yPos += 6;
  }
  
  yPos += 10;
  
  doc.setFillColor(20, 184, 166);
  doc.rect(15, yPos, pageWidth - 30, 15, 'F');
  
  yPos += 10;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`Amount Paid: $${receipt.amount.toFixed(2)}`, pageWidth / 2, yPos, { align: 'center' });
  
  doc.setTextColor(0, 0, 0);
  yPos += 20;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text('Thank you for your payment!', pageWidth / 2, yPos, { align: 'center' });
  yPos += 5;
  doc.text('This is a computer-generated receipt and does not require a signature.', pageWidth / 2, yPos, { align: 'center' });
  
  return doc;
};

const generateDetailedReceipt = (doc: jsPDF, receipt: Receipt) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  doc.setFillColor(20, 184, 166);
  doc.rect(0, 0, pageWidth, 30, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(26);
  doc.setFont('helvetica', 'bold');
  doc.text('PAYMENT RECEIPT', pageWidth / 2, 15, { align: 'center' });
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('MedSystem Hospital Management System', pageWidth / 2, 23, { align: 'center' });
  
  doc.setTextColor(0, 0, 0);
  yPos = 45;
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Receipt Information', 20, yPos);
  
  yPos += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  doc.text(`Receipt Number:`, 20, yPos);
  doc.setFont('helvetica', 'bold');
  doc.text(receipt.receipt_number, 70, yPos);
  
  doc.setFont('helvetica', 'normal');
  doc.text(`Receipt Type:`, 120, yPos);
  doc.setFont('helvetica', 'bold');
  doc.text(receipt.receipt_type.toUpperCase(), 160, yPos);
  
  yPos += 7;
  doc.setFont('helvetica', 'normal');
  doc.text(`Date Issued:`, 20, yPos);
  doc.setFont('helvetica', 'bold');
  doc.text(new Date(receipt.generated_at).toLocaleDateString(), 70, yPos);
  
  doc.setFont('helvetica', 'normal');
  doc.text(`Time:`, 120, yPos);
  doc.setFont('helvetica', 'bold');
  doc.text(new Date(receipt.generated_at).toLocaleTimeString(), 160, yPos);
  
  yPos += 15;
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Patient Information', 20, yPos);
  
  yPos += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  if (receipt.patient) {
    doc.text(`Name:`, 20, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text(receipt.patient.name, 70, yPos);
    
    yPos += 7;
    doc.setFont('helvetica', 'normal');
    if (receipt.patient.unique_id) {
      doc.text(`Patient ID:`, 20, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(receipt.patient.unique_id, 70, yPos);
      yPos += 7;
    }
    
    doc.setFont('helvetica', 'normal');
    if (receipt.patient.phone) {
      doc.text(`Phone:`, 20, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(receipt.patient.phone, 70, yPos);
      yPos += 7;
    }
    
    if (receipt.patient.email) {
      doc.setFont('helvetica', 'normal');
      doc.text(`Email:`, 20, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(receipt.patient.email, 70, yPos);
      yPos += 7;
    }
  }
  
  yPos += 10;
  
  if (receipt.invoice) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice Details', 20, yPos);
    
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    doc.text(`Invoice Number:`, 20, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text(receipt.invoice.invoice_number, 70, yPos);
    
    yPos += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice Date:`, 20, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text(new Date(receipt.invoice.issue_date).toLocaleDateString(), 70, yPos);
    
    yPos += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice Status:`, 20, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text(receipt.invoice.status.toUpperCase(), 70, yPos);
    
    yPos += 15;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Financial Breakdown', 20, yPos);
    
    yPos += 8;
    doc.setFontSize(10);
    
    doc.setFillColor(240, 240, 240);
    doc.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Description', 25, yPos);
    doc.text('Amount', pageWidth - 50, yPos, { align: 'right' });
    
    yPos += 10;
    doc.setFont('helvetica', 'normal');
    
    doc.text('Subtotal', 25, yPos);
    doc.text(`$${receipt.invoice.subtotal.toFixed(2)}`, pageWidth - 50, yPos, { align: 'right' });
    yPos += 7;
    
    if (receipt.invoice.discount_amount && receipt.invoice.discount_amount > 0) {
      doc.setTextColor(34, 197, 94); // Green
      doc.text('Discount', 25, yPos);
      doc.text(`-$${receipt.invoice.discount_amount.toFixed(2)}`, pageWidth - 50, yPos, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      yPos += 7;
    }
    
    if (receipt.invoice.tax_amount > 0) {
      doc.text(`Tax (${receipt.invoice.tax_rate}%)`, 25, yPos);
      doc.text(`$${receipt.invoice.tax_amount.toFixed(2)}`, pageWidth - 50, yPos, { align: 'right' });
      yPos += 7;
    }
    
    doc.setLineWidth(0.5);
    doc.line(20, yPos, pageWidth - 20, yPos);
    yPos += 7;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Total Amount', 25, yPos);
    doc.text(`$${receipt.invoice.total.toFixed(2)}`, pageWidth - 50, yPos, { align: 'right' });
    yPos += 7;
    
    if (receipt.invoice.amount_paid > receipt.amount) {
      doc.setFont('helvetica', 'normal');
      doc.text('Previous Payments', 25, yPos);
      doc.text(`$${(receipt.invoice.amount_paid - receipt.amount).toFixed(2)}`, pageWidth - 50, yPos, { align: 'right' });
      yPos += 7;
    }
  }
  
  yPos += 5;
  
  doc.setFillColor(20, 184, 166);
  doc.rect(20, yPos, pageWidth - 40, 20, 'F');
  
  yPos += 8;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('AMOUNT PAID', 25, yPos);
  doc.setFontSize(16);
  doc.text(`$${receipt.amount.toFixed(2)}`, pageWidth - 50, yPos, { align: 'right' });
  
  yPos += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  if (receipt.payment_method) {
    doc.text(`Payment Method: ${receipt.payment_method.toUpperCase()}`, 25, yPos);
  }
  
  doc.setTextColor(0, 0, 0);
  yPos += 20;
  
  if (receipt.invoice && receipt.invoice.balance_due > 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Remaining Balance:', 25, yPos);
    doc.setTextColor(239, 68, 68); // Red
    doc.text(`$${receipt.invoice.balance_due.toFixed(2)}`, pageWidth - 50, yPos, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    yPos += 10;
  }
  
  yPos += 10;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  doc.text('Thank you for your payment!', pageWidth / 2, yPos, { align: 'center' });
  yPos += 5;
  doc.text('For any queries, please contact our billing department.', pageWidth / 2, yPos, { align: 'center' });
  yPos += 5;
  doc.text('This is a computer-generated receipt and does not require a signature.', pageWidth / 2, yPos, { align: 'center' });
  
  return doc;
};

const generateSummaryReceipt = (doc: jsPDF, receipt: Receipt) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 15;

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('RECEIPT', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 8;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('MedSystem Hospital', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 15;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(receipt.receipt_number, pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(new Date(receipt.generated_at).toLocaleString(), pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 15;
  
  doc.setLineWidth(0.5);
  doc.line(20, yPos, pageWidth - 20, yPos);
  yPos += 10;
  
  if (receipt.patient) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(receipt.patient.name, pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    
    if (receipt.patient.unique_id) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`ID: ${receipt.patient.unique_id}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
    } else {
      yPos += 5;
    }
  }
  
  if (receipt.invoice) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice: ${receipt.invoice.invoice_number}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
  }
  
  doc.setFillColor(20, 184, 166);
  doc.roundedRect(30, yPos, pageWidth - 60, 25, 3, 3, 'F');
  
  yPos += 10;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('PAID', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 8;
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`$${receipt.amount.toFixed(2)}`, pageWidth / 2, yPos, { align: 'center' });
  
  doc.setTextColor(0, 0, 0);
  yPos += 15;
  
  if (receipt.payment_method) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`via ${receipt.payment_method.toUpperCase()}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
  }
  
  doc.setLineWidth(0.5);
  doc.line(20, yPos, pageWidth - 20, yPos);
  yPos += 10;
  
  if (receipt.invoice && receipt.invoice.balance_due > 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Remaining Balance:', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(239, 68, 68);
    doc.text(`$${receipt.invoice.balance_due.toFixed(2)}`, pageWidth / 2, yPos, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    yPos += 10;
  }
  
  yPos += 10;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text('Thank you!', pageWidth / 2, yPos, { align: 'center' });
  yPos += 5;
  doc.text('Computer-generated receipt', pageWidth / 2, yPos, { align: 'center' });
  
  return doc;
};

export const printReceipt = (receipt: Receipt, template: 'standard' | 'detailed' | 'summary' = 'standard') => {
  const doc = generateReceiptPDF(receipt, template);
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};

export const downloadReceipt = (receipt: Receipt, template: 'standard' | 'detailed' | 'summary' = 'standard') => {
  const doc = generateReceiptPDF(receipt, template);
  doc.save(`Receipt-${receipt.receipt_number}.pdf`);
};

export const getReceiptPDFBlob = (receipt: Receipt, template: 'standard' | 'detailed' | 'summary' = 'standard'): Blob => {
  const doc = generateReceiptPDF(receipt, template);
  return doc.output('blob');
};
